import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { Plus, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SavedViews } from "@/components/SavedViews";
import { SortHeader } from "@/components/SortHeader";
import { sortRows, useSort, asNumber } from "@/lib/sorting";
import { ProjectDetailSheet } from "@/components/ProjectDetailSheet";
import { ProjectFormDialog } from "@/components/ProjectFormDialog";
import { ExportButton } from "@/components/ExportButton";
import { MultiSelect } from "@/components/MultiSelect";
import { SearchBox } from "@/components/SearchBox";
import { ColumnPicker } from "@/components/ColumnPicker";
import { PROJECT_COLUMNS, ProjectRow } from "@/components/ProjectsRow";
import { toast } from "sonner";
import { api, apiError, fmtCr } from "@/lib/api";
import { useFreshest } from "@/lib/freshest";
import { useAuth } from "@/context/AuthContext";

const PROGRESS_OPTIONS = [
  { value: "on_track", label: "On Track", hint: "no date on it has gone past" },
  { value: "delayed", label: "Delayed", hint: "a sub-stage date or the committed date has gone past" },
];
const PROGRESS_LABELS = Object.fromEntries(PROGRESS_OPTIONS.map((o) => [o.value, o.label]));
const PROGRESS_RANK = { delayed: 0, on_track: 1 };
const COLUMN_KEY = "sail_dx_project_columns";
// The slicers that carry one or many picks, by their name in the address bar.
const SLICERS = ["plant", "status", "term", "provider", "progress", "phase", "type", "area",
                 "tech", "dept", "window"];

const dayKey = (value) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value || ""));
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : null;
};

const pickProject = (p, key) => {
  if (key === "progress") return PROGRESS_RANK[p.register_progress] ?? 9;
  // A use case with no figure against it is not a zero-crore use case — it sits at the bottom.
  if (key === "est_value_cr") return asNumber(p.est_value_cr) || null;
  if (key === "compliance")
    return asNumber(p.compliance?.latest_pct ?? p.compliance?.cumulative_pct) || null;
  if (key === "vetted_cr") return asNumber(p.impact?.actual_cr) || null;
  if (key === "responsible") return p.owner_name || p.responsible || "";
  if (key === "status") return p.status_view || p.status;
  if (key === "phase") return p.phase_view || "";
  if (["start_on", "target_completion_on", "last_updated_on"].includes(key))
    return dayKey(p[key]);
  return p[key];
};

const options = (values = [], help = {}) =>
  values.map((v) => ({ value: v, label: v, hint: help?.[v] }));

export default function Projects() {
  const { scopedPlant, canReadAll, isPartner, shows, writable } = useAuth();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moreSlicers, setMoreSlicers] = useState(false);
  const { sort, on: sortBy, set: setSort } = useSort("projects");
  const { start, isFresh } = useFreshest();

  const [hidden, setHidden] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLUMN_KEY));
      return Array.isArray(saved) ? saved : PROJECT_COLUMNS.filter((c) => c.off).map((c) => c.key);
    } catch {
      return PROJECT_COLUMNS.filter((c) => c.off).map((c) => c.key);
    }
  });
  const columns = PROJECT_COLUMNS.filter((c) => !c.needs || shows(c.needs));
  const shownColumns = columns.filter((c) => !hidden.includes(c.key));
  const toggleColumn = (key) => {
    const next = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    setHidden(next);
    localStorage.setItem(COLUMN_KEY, JSON.stringify(next));
  };
  const resetColumns = () => {
    const next = PROJECT_COLUMNS.filter((c) => c.off).map((c) => c.key);
    setHidden(next);
    localStorage.setItem(COLUMN_KEY, JSON.stringify(next));
  };

  const get = (key) => params.get(key) || "";
  const plant = get("plant") || scopedPlant || "";
  const status = get("status");
  const term = get("term");
  const provider = get("provider");
  const progress = get("progress");
  const type = get("type");
  const phase = get("phase");
  const area = get("area");
  const tech = get("tech");
  const dept = get("dept");
  const window_ = get("window");

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const queryParams = {
    plant,
    status,
    term,
    provider_type: provider,
    progress,
    project_type: type,
    phase,
    focus_area: area,
    technology_area: tech,
    dept_shop: dept,
    window: window_,
    q,
  };

  const windowLabel = (key) =>
    (meta?.time_windows || []).find((w) => w.key === key)?.label || key;

  const applied = [
    ["Unit", plant],
    ["Status", status],
    ["Term", term],
    ["Provider", provider],
    ["How it is going", progress.split(",").filter(Boolean).map((p) => PROGRESS_LABELS[p]).join(", ")],
    ["Sub-stage", phase],
    ["Project type", type],
    ["Application area", area],
    ["Technology area", tech],
    ["Department / Mine", dept],
    ["Time", window_.split(",").filter(Boolean).map(windowLabel).join(", ")],
    ...(q ? [["Search", q]] : []),
  ].filter(([, value]) => value && value !== "all");

  const load = () => {
    // The newest request wins. A 2 MB unfiltered register takes a few seconds to arrive, and its
    // reply used to land on top of the filtered list an officer had already asked for.
    const ticket = start();
    setLoading(true);
    // What each slicer can still offer, given everything else already picked — so a pick that
    // would bring nothing back is never offered.
    api
      .get("/projects/facets", { params: queryParams })
      .then((r) => isFresh(ticket) && setFacets(r.data))
      .catch(() => {});
    api
      .get("/projects", { params: queryParams })
      .then((r) => {
        if (!isFresh(ticket)) return;
        setRows(r.data);
        setLoading(false);
      })
      .catch((e) => {
        if (!isFresh(ticket)) return;
        setLoading(false);
        toast.error(apiError(e));
      });
  };

  const clearFilters = () => {
    setQ("");
    setParams(new URLSearchParams());
  };

  // A saved view carries the slicers by their URL names, so a view is also a shareable link.
  const viewFilters = {
    plant, status, term, provider, progress, phase, type, area, tech, dept, window: window_,
    q,
    sort: sort.key,
    dir: sort.key ? sort.dir : "",
  };

  const applyView = (saved) => {
    const next = new URLSearchParams();
    SLICERS.forEach((key) => {
      if (saved[key] && saved[key] !== "all") next.set(key, saved[key]);
    });
    setParams(next);
    setQ(saved.q || "");
    setSort({ key: saved.sort || "", dir: saved.dir === "desc" ? "desc" : "asc" });
  };

  const suggestions = [
    ...(scopedPlant || meta?.my_plant
      ? [{
          name: `${scopedPlant || meta.my_plant} — ongoing`,
          filters: { plant: scopedPlant || meta.my_plant, status: "Ongoing" },
        }]
      : [{ name: "Ongoing everywhere", filters: { status: "Ongoing" } }]),
    { name: "Expected this quarter", filters: { window: "expected_this_quarter" } },
    { name: "Target value — running late", filters: { type: "Pravartanam-TV", progress: "delayed" } },
    { name: "Everything running late", filters: { progress: "delayed" } },
  ];

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, status, term, provider, progress, type, phase, area, tech, dept, window_, q]);

  // The search word travels in the address too, so what is on screen is always a link that can be
  // sent on or kept as a view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const benefit = rows.reduce((s, r) => s + (r.est_value_cr || 0), 0);
  const count = (key) => facets?.facets?.[key];
  // A slicer offers what the register actually holds and what the other picks still leave open. A
  // word the register carries but the portal's own list does not — an old pipeline word, a unit's
  // own application area — is still offered, or those rows could never be sliced out.
  const withHeld = (list, counts) => {
    if (!counts) return list;
    const known = new Set(list.map((o) => o.value));
    return [
      ...list,
      ...Object.keys(counts)
        .filter((v) => !known.has(v))
        .sort()
        .map((v) => ({ value: v, label: v, hint: "as the register holds it" })),
    ];
  };
  // A mine group is worked as a set of individual mines, and those mines are its departments — so
  // the slicer offers every group's own mines, named by the group they belong to. Pick a unit and
  // it offers that unit's mines alone.
  const unitsPicked = plant.split(",").filter(Boolean);
  const mineOptions = Object.entries(meta?.sub_divisions || {})
    .filter(([unit]) => !unitsPicked.length || unitsPicked.includes(unit))
    .flatMap(([unit, mines]) =>
      (mines || []).map((m) => ({ value: m, label: m, hint: `${unit} — a mine of this group` })),
    );
  // **The sub-stage slicer follows the status, and nothing outside that status's own road is
  // offered.** A sub-stage only ever belongs to a usecase that is Identified or Ongoing: picking
  // Identified offers the three sub-stages of being identified and those three alone, and picking
  // Ongoing offers the working road. Where the project type or the provider already settles which
  // road a usecase travels, only that road's stages are offered — the same rule the portal applies
  // to a usecase itself. A sub-stage the register holds off-road is not a pick; it is named on the
  // Data Inconsistency screen instead.
  const statusesPicked = status.split(",").filter(Boolean);
  const typesPicked = type.split(",").filter(Boolean);
  const providersPicked = provider.split(",").filter(Boolean);
  const roadKey =
    typesPicked.length && typesPicked.every((t) => t.startsWith("Pravartanam-TV"))
      ? "delivery"
      : !providersPicked.length
        ? ""
        : providersPicked.every((p) => p === "In-house")
          ? "self"
          : providersPicked.every((p) => p !== "In-house")
            ? "procurement"
            : "";
  const identifiedStages = options(
    meta?.identified_sub_stages || [],
    meta?.identified_sub_stage_help,
  ).map((o) => ({ ...o, hint: o.hint || "while identified" }));
  const workingStages = (meta?.roads || [])
    .filter((r) => !roadKey || r.key === roadKey)
    .flatMap((r) => options(r.stages || []).map((o) => ({ ...o, hint: r.label })));
  const forStatus = (s) =>
    s === "Identified" ? identifiedStages : s === "Ongoing" ? workingStages : [];
  const subStageOptions = (
    statusesPicked.length
      ? statusesPicked.flatMap(forStatus)
      : [...identifiedStages, ...workingStages]
  ).filter((o, i, all) => all.findIndex((x) => x.value === o.value) === i);
  const subStageAlways = subStageOptions.map((o) => o.value);
  const subStageNote = statusesPicked.length && !subStageOptions.length;

  return (
    <div className="p-5 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Project register</div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            All digitalisation projects
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {rows.length} project(s) · expected benefit {fmtCr(benefit)}. Click a project to see the
            full details, add daily readings and update the status. Anything missing, conflicting or
            at odds with its own dates is chased on{" "}
            <Link
              to="/inconsistency"
              data-testid="projects-to-inconsistency"
              className="text-primary underline decoration-dotted underline-offset-2"
            >
              Data Inconsistency
            </Link>
            .
          </p>
          <div
            className="mt-2 flex flex-wrap items-center gap-2 text-xs"
            data-testid="projects-filter-summary"
          >
            <span className="font-display font-bold uppercase tracking-widest text-[10px]">
              {loading
                ? "Updating the list…"
                : `Showing ${rows.length}${
                    meta?.project_total > rows.length ? ` of ${meta.project_total}` : ""
                  }`}
            </span>
            {applied.map(([label, value]) => (
              <span
                key={label}
                data-testid={`applied-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="border border-primary px-2 py-0.5 text-[11px] text-primary"
              >
                {label}: {value}
              </span>
            ))}
            {applied.length > 0 && (
              <button
                type="button"
                data-testid="clear-filters"
                onClick={clearFilters}
                className="underline decoration-dotted text-[11px] text-muted-foreground transition-colors duration-200 hover:text-foreground"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {meta?.can_add && shows("projects.add") && writable("projects") && (
            <Button
              data-testid="add-project-button"
              onClick={() => setAdding(true)}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              <Plus className="mr-2 h-4 w-4" /> Add a use case
            </Button>
          )}
          <ColumnPicker
            columns={columns}
            shown={shownColumns.map((c) => c.key)}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
          {shows("projects.export") && (
            <ExportButton
              path="/projects/export"
              params={queryParams}
              testId="export-projects"
              label="Download this view"
            />
          )}
        </div>
      </div>

      <SavedViews
        screen="projects"
        filters={viewFilters}
        onApply={applyView}
        suggestions={suggestions}
      />

      <div className="mt-6 flex flex-wrap gap-3">
        <SearchBox value={q} onChange={setQ} params={{ plant }} />
        <MultiSelect
          label="Unit"
          testId="filter-plant"
          width="w-[150px]"
          value={plant}
          onChange={(v) => setParam("plant", v)}
          counts={count("plant")}
          options={(meta?.plants || []).map((p) => ({ value: p.code, label: p.code, hint: p.name }))}
        />
        <MultiSelect
          label="Status"
          testId="filter-status"
          width="w-[190px]"
          value={status}
          onChange={(v) => setParam("status", v)}
          counts={count("status")}
          options={(meta?.status_views || []).map((s) => ({
            value: s.key,
            label: s.key,
            hint: s.help,
          }))}
        />
        <MultiSelect
          label={subStageNote ? "Sub-stage — none applies" : "Sub-stage"}
          testId="filter-phase"
          width="w-[200px]"
          value={phase}
          onChange={(v) => setParam("phase", v)}
          counts={count("phase")}
          always={subStageAlways}
          options={statusesPicked.length ? subStageOptions : withHeld(subStageOptions, count("phase"))}
        />
        <MultiSelect
          label="Time window"
          testId="filter-window"
          width="w-[200px]"
          value={window_}
          onChange={(v) => setParam("window", v)}
          counts={facets?.windows}
          options={(meta?.time_windows || []).map((w) => ({
            value: w.key,
            label: w.label,
            hint: `${w.window} · ${w.from} to ${w.to}`,
          }))}
        />
        <MultiSelect
          label="How it is going"
          testId="filter-progress"
          width="w-[180px]"
          value={progress}
          onChange={(v) => setParam("progress", v)}
          counts={count("progress")}
          options={PROGRESS_OPTIONS}
        />
        <button
          type="button"
          data-testid="more-slicers"
          onClick={() => setMoreSlicers((v) => !v)}
          className="flex h-11 items-center gap-2 border border-border bg-card px-4 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {moreSlicers ? "Fewer slicers" : "More slicers"}
        </button>
      </div>

      {moreSlicers && (
        <div className="mt-3 flex flex-wrap gap-3" data-testid="more-slicers-row">
          <MultiSelect
            label="Term"
            testId="filter-term"
            width="w-[170px]"
            value={term}
            onChange={(v) => setParam("term", v)}
            counts={count("term")}
            options={options(meta?.terms || [], meta?.term_help)}
          />
          <MultiSelect
            label="Solution provider"
            testId="filter-provider"
            width="w-[190px]"
            value={provider}
            onChange={(v) => setParam("provider", v)}
            counts={count("provider_type")}
            options={options(meta?.provider_types || [], meta?.provider_type_help)}
          />
          <MultiSelect
            label={isPartner ? "Pravartanam-TV only" : "Project type"}
            testId="filter-type"
            width="w-[200px]"
            value={type}
            onChange={(v) => setParam("type", v)}
            counts={count("project_type")}
            options={withHeld(options(meta?.project_types || []), count("project_type"))}
          />
          <MultiSelect
            label="Application area"
            testId="filter-area"
            width="w-[210px]"
            value={area}
            onChange={(v) => setParam("area", v)}
            counts={count("focus_area")}
            options={withHeld(options(meta?.focus_areas || []), count("focus_area"))}
          />
          <MultiSelect
            label="Technology area"
            testId="filter-tech"
            width="w-[210px]"
            value={tech}
            onChange={(v) => setParam("tech", v)}
            counts={count("technology_area")}
            options={withHeld(options(meta?.technology_areas || []), count("technology_area"))}
          />
          <MultiSelect
            label="Department / Mine"
            testId="filter-dept"
            width="w-[210px]"
            value={dept}
            onChange={(v) => setParam("dept", v)}
            counts={count("dept_shop")}
            options={withHeld(mineOptions, count("dept_shop"))}
          />
          {(!scopedPlant || canReadAll) && (
            <p className="flex h-11 items-center text-[11px] text-muted-foreground">
              Leave a slicer empty to see everything it covers. Each one offers only what your
              other picks still leave open.
            </p>
          )}
        </div>
      )}

      {!writable("projects") && (
        <p
          className="mt-4 border-l-2 border-[hsl(var(--watch))] pl-3 text-xs text-muted-foreground"
          data-testid="projects-read-only"
        >
          The register is open to you for reading only. SDTD can change that.
        </p>
      )}

      <div className="mt-6 overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[1240px] text-sm" data-testid="projects-table">
          <thead className="bg-muted">
            <tr className="text-left">
              {shownColumns.map((c) => (
                <th key={c.key} className="label-caps px-4 py-3 font-bold">
                  {c.sortable === false ? (
                    c.label
                  ) : (
                    <SortHeader label={c.label} column={c.key} sort={sort} onSort={sortBy} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortRows(rows, sort.key, sort.dir, pickProject).map((p) => (
              <ProjectRow
                key={p.id}
                p={p}
                columns={shownColumns.map((c) => c.key)}
                onOpen={setOpenId}
              />
            ))}
            {!rows.length && (
              <tr>
                <td
                  colSpan={shownColumns.length}
                  className="p-8 text-center text-muted-foreground"
                >
                  No projects match what you selected.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ProjectDetailSheet
        projectId={openId}
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        meta={meta}
        onChanged={load}
      />

      <ProjectFormDialog
        open={adding}
        onOpenChange={setAdding}
        project={null}
        meta={meta}
        onSaved={(p) => {
          load();
          if (p?.id) setOpenId(p.id);
        }}
      />
    </div>
  );
}
